import { api } from './api';

export interface TrendItem {
  query: string;
  search_volume: number | null;
  increase_percentage: number | null;
  active: boolean;
  start_timestamp: number | null;
  trend_breakdown: string[];
  serpapi_news_link: string | null;
  // Coverage Score
  score: number;
  band: 'alta' | 'media' | 'baixa' | string;
  editoria: string | null;
  audiencia: number;
  momentum: number;
  fit: number;
  confianca: number;
  flags: string[];
}

export interface TrendsResponse {
  items: TrendItem[];
  geo: string;
  hours: number;
  total: number;
  // Presente só quando o backend NÃO conseguiu buscar (sem chave SERP): aí
  // items:[] não significa "nada em alta", significa "não houve busca".
  // Opcional — backend sem o campo continua válido.
  aviso?: string | null;
}

export interface NewsCard {
  title: string;
  source: string | null;
  source_icon: string | null;
  link: string | null;
  date: string | null;
  thumbnail: string | null;
}

export interface TopicDetail {
  query: string;
  score: number;
  band: string;
  editoria: string | null;
  verdict: 'FURO' | 'COBRIR' | 'SATURADO' | 'IGNORAR' | string;
  motivo: string;
  confianca: number;
  flags: string[];
  num_sources: number;
  // preenchido quando a busca de cobertura FALHOU: num_sources=0 vira "não medi"
  cobertura_indisponivel?: string | null;
  news: NewsCard[];
  paa: string[];
  keywords: string[];
  trend_breakdown: string[];
  search_volume: number | null;
  increase_percentage: number | null;
  intel_id?: string | null; // B-13: id do snapshot persistido (handoff por referência)
  /** "0 matérias" só é furo quando o provedor respondeu (03/09). */
  coverage_status?: 'ok' | 'unavailable';
  coverage_reason?: string | null; // serp_key_missing | serp_quota | serp_error:<msg>
}

export interface PautaResponse {
  id: number;
  tema: string;
  fonte: string;
  estado: string;
  nivel_autonomia: string;
  created_at: string;
  dados_origem?: { editoria?: string; score?: number; band?: string; origem?: string } | null;
}

export interface TrendFilters {
  geo?: string;
  hours?: number;
  category?: string;
  onlyActive?: boolean;
}

export const intelligenceService = {
  getTrends: (f: TrendFilters = {}): Promise<TrendsResponse> => {
    const qs = new URLSearchParams();
    qs.set('geo', f.geo ?? 'BR');
    qs.set('hours', String(f.hours ?? 24));
    if (f.category) qs.set('category', f.category);
    if (f.onlyActive) qs.set('only_active', 'true');
    return api.get<TrendsResponse>(`/intelligence/trends?${qs}`);
  },
  getTopic: (item: {
    query: string;
    search_volume?: number | null;
    increase_percentage?: number | null;
    editoria?: string | null;
    // A nota que o card já mostrou viaja junto: sem ela o backend recalcula com
    // active=true fixo e categorias vazias, e card e detalhe discordam sobre o
    // mesmo termo. Campos opcionais — o backend antigo simplesmente os ignora.
    score?: number;
    confianca?: number;
    flags?: string[];
    active?: boolean;
  }): Promise<TopicDetail> => {
    const qs = new URLSearchParams();
    qs.set('query', item.query);
    if (item.search_volume != null) qs.set('search_volume', String(item.search_volume));
    if (item.increase_percentage != null)
      qs.set('increase_percentage', String(item.increase_percentage));
    if (item.editoria) qs.set('editoria', item.editoria);
    if (item.score != null) qs.set('score', String(item.score));
    if (item.confianca != null) qs.set('confianca', String(item.confianca));
    if (item.flags?.length) qs.set('flags', item.flags.join(','));
    if (item.active != null) qs.set('active', String(item.active));
    return api.get<TopicDetail>(`/intelligence/topic?${qs}`);
  },
  listPautas: (): Promise<PautaResponse[]> => api.get<PautaResponse[]>('/intelligence/pautas'),

  // ─── Fluxo de Quentes (esteira automática) ───────────────────────────────
  getSerpProvider: (): Promise<{ provider: string }> =>
    api.get<{ provider: string }>('/intelligence/serp-provider'),
  setSerpProvider: (provider: string): Promise<{ provider: string }> =>
    api.put<{ provider: string }>('/intelligence/serp-provider', { provider }),

  getQuentesConfig: (): Promise<QuentesConfig> =>
    api.get<QuentesConfig>('/intelligence/quentes/config'),
  saveQuentesConfig: (cfg: QuentesConfig): Promise<QuentesConfig> =>
    api.put<QuentesConfig>('/intelligence/quentes/config', cfg),
  runQuentes: (geo?: string): Promise<QuentesRunResult> =>
    api.post<QuentesRunResult>(
      `/intelligence/quentes/run?force=true${geo ? `&geo=${encodeURIComponent(geo)}` : ''}`,
      {},
    ),
};

export interface QuentesEditoria {
  enabled: boolean;
  cap_dia: number;
  wp_category_id: number | null;
}

export interface QuentesConfig {
  enabled: boolean;
  wp_site: string | null; // destino explícito (slug do site WP) — sem fallback
  geo: string;
  hours: number;
  score_cut: number;
  min_sources: number;
  publish_status: 'draft' | 'publish' | string;
  active_start: number;
  active_end: number;
  max_per_cycle: number;
  kill_switch: boolean;
  editorias: Record<string, QuentesEditoria>;
}

export interface QuentesProduced {
  tema: string;
  editoria: string;
  score: number;
  wp_id: number;
  wp_link: string;
  status: string;
  sources: number;
}

export interface QuentesPulo {
  tema: string;
  motivo: string;
}

export interface QuentesRunResult {
  ranked?: number;
  picked?: number;
  produced?: QuentesProduced[];
  /** Temas que o ciclo descartou um a um. SEMPRE lista — o motivo de um ciclo
   *  que nem começou vem em `skipped_reason`, nunca aqui (F-251). */
  skipped?: QuentesPulo[];
  /** Preenchido quando o ciclo foi pulado inteiro: kill switch, esteira
   *  desligada, fora da janela ativa, ou sem destino escolhido. */
  skipped_reason?: string;
  publish_status?: string;
}
